import { Box, Card, Tabs } from '@nrx/ui';
import { type JSX, useMemo, useRef } from 'react';
import { useParams } from 'react-router';

import { getApiBaseUrl } from '../lib/api-base.js';
import { authFetchWithExchangeRetry } from '../lib/auth-fetch-with-exchange-retry.js';
import { reportConsultError } from '../lib/report-consult-error.js';

import { ConsultContentShell, consultHeroVariant } from './consult-content-shell.js';
import type { ConsultTab } from './consult-tabs.js';
import { getVisibleTabs } from './consult-tabs.js';
import { ConsultExchangeFailed } from './exchange/consult-exchange-failed.js';
import { ConsultLoadingState } from './exchange/consult-loading-state.js';
import { useConsultExchange } from './exchange/use-consult-exchange.js';
import { oneToOneSubsections } from './ffs/one-to-one-subsections.js';
import { GroupConsultDayOverlay } from './group-consults/group-consult-day-overlay.js';
import { GroupConsultTab } from './group-consults/group-consult-tab.js';
import { useGroupConsults } from './group-consults/use-group-consults.js';
import { BookingReportContext } from './one-to-one/booking-report.js';
import { OneToOneTab } from './one-to-one/one-to-one-tab.js';
import { toZoomBookingMember } from './one-to-one/to-zoom-booking-member.js';
import { useOneToOneConsultants } from './one-to-one/use-one-to-one-consultants.js';
import { RegisterWebinarModal } from './register/register-webinar-modal.js';
import { RegistrationConfirmationDialog } from './register/registration-confirmation-dialog.js';
import { useWebinarRegistration } from './register/use-webinar-registration.js';

const API_BASE = getApiBaseUrl();

/**
 * Consult scheduling landing page for a magic link (`/consult-scheduling/:token`).
 *
 * @remarks Composition only: the exchange decides which tabs exist ({@link getVisibleTabs}), and
 * each tab's body, data loading and modals live in
 * `features/{exchange,group-consults,one-to-one,ffs,register}/`.
 */
export default function ConsultSchedulingPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const {
    jwt,
    setJwt,
    status,
    error,
    exchangeFailTitle,
    exchangeFailRetryable,
    consultType,
    consultStage,
    memberDetails,
    taskAssignee,
    activeTab,
    setActiveTab,
    adminFeeCardRequired,
    staffLink,
    reExchange,
  } = useConsultExchange({ token, apiBase: API_BASE });

  const visibleTabs = getVisibleTabs(consultType);
  const scheduleGroupTabVisible = visibleTabs.includes('group');
  const scheduleOneToOneTabVisible = visibleTabs.includes('1:1');
  const showConsultTabBar = visibleTabs.length > 1;

  const group = useGroupConsults({
    jwt,
    setJwt,
    token,
    apiBase: API_BASE,
    enabled: scheduleGroupTabVisible,
    activeTab,
  });
  const consultants = useOneToOneConsultants({
    jwt,
    setJwt,
    token,
    apiBase: API_BASE,
    enabled: scheduleOneToOneTabVisible,
  });
  const registration = useWebinarRegistration({
    jwt,
    setJwt,
    token,
    apiBase: API_BASE,
    events: group.events,
    displayTimeZone: group.displayTimeZone,
    memberDetails,
  });

  const member = toZoomBookingMember(memberDetails);

  // Fire-and-forget: the member's booking already took in Zoom, and care-hooks' hourly sync applies
  // it to CareBetty if this report never lands. No reporter until the exchange has a session.
  // Each booking is reported once, however many frames or messages announce it: concurrent reports
  // of one booking race each other in care-hooks.
  const reported = useRef(new Set<string>());
  const reportBooking = useMemo(
    () =>
      jwt
        ? (scheduledEventId: string) => {
            if (reported.current.has(scheduledEventId)) return;
            reported.current.add(scheduledEventId);
            // Fires at the end of the member's time in the frame, often past the session's expiry.
            authFetchWithExchangeRetry({
              magicLinkToken: token,
              setJwt,
              apiBase: API_BASE,
              jwt,
              url: `${API_BASE}/consult-scheduling/bookings/one-to-one`,
              init: {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ scheduledEventId }),
              },
            })
              .then((res) => {
                if (!res.ok) throw new Error(`booking report failed (${res.status})`);
              })
              .catch((err: unknown) => reportConsultError('booking-report', err));
          }
        : null,
    [jwt, setJwt, token],
  );

  const oneToOne = oneToOneSubsections({
    consultType,
    consultStage,
    hasInitialScheduleUrl: Boolean(consultants.initialConsultScheduleUrl.trim()),
    hasProgramFollowUpUrl: Boolean(consultants.followUpConsultScheduleUrl.trim()),
  });

  if (status === 'loading') {
    return <ConsultLoadingState />;
  }

  if (status === 'exchange-failed') {
    return (
      <ConsultExchangeFailed
        title={exchangeFailTitle}
        message={error}
        onRetry={exchangeFailRetryable ? reExchange : undefined}
      />
    );
  }

  const heroVariant = consultHeroVariant(consultType);

  const groupTabBody = (
    <GroupConsultTab
      group={group}
      showRegisterButton={Boolean(jwt)}
      registerLoading={registration.registerLoading}
      onRegisterClick={registration.handleRegisterClick}
    />
  );

  const oneToOneTabBody = (
    <OneToOneTab
      consultType={consultType}
      subsections={oneToOne}
      taskAssignee={taskAssignee}
      consultants={consultants}
      member={member}
      adminFeeCardRequired={adminFeeCardRequired}
      staffLink={staffLink}
      jwt={jwt}
      onAuthorized={reExchange}
    />
  );

  return (
    <BookingReportContext.Provider value={reportBooking}>
      <ConsultContentShell heroVariant={heroVariant}>
        <Box>
          {/*
          One visible mode means no tablist, so its body must not be a `Tabs.Panel` either — the
          panel's `aria-labelledby` would name a tab that was never rendered.
        */}
          {showConsultTabBar ? (
            <Tabs.Root
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as ConsultTab)}
            >
              <Tabs.List aria-label="Scheduling options" className="consult-tabbar">
                <Tabs.Tab value="group" className="consult-tabbar__tab">
                  Group Consults
                </Tabs.Tab>
                <Tabs.Tab value="1:1" className="consult-tabbar__tab">
                  1:1 Consults
                </Tabs.Tab>
              </Tabs.List>

              <Card elevation="soft" className="consult-tabpanel">
                <Tabs.Panel value="group" className="consult-tabpanel__panel">
                  {groupTabBody}
                </Tabs.Panel>
                <Tabs.Panel value="1:1" className="consult-tabpanel__panel">
                  {oneToOneTabBody}
                </Tabs.Panel>
              </Card>
            </Tabs.Root>
          ) : (
            <Card elevation="soft" className="consult-tabpanel">
              {activeTab === 'group' ? groupTabBody : oneToOneTabBody}
            </Card>
          )}
        </Box>

        {activeTab === 'group' && group.groupViewMode === 'month' && group.selectedGroupDay ? (
          <GroupConsultDayOverlay
            selectedDay={group.selectedGroupDay}
            timeZone={group.displayTimeZone}
            showRegisterButton={Boolean(jwt)}
            registerLoading={registration.registerLoading}
            /*
            Registering CLOSES this overlay rather than opening the register modal on top of it.
            `@nrx/ui`'s focus trap saves `document.body.style.overflow` per instance and restores
            that saved value on cleanup, with no refcount — so with both mounted the inner trap has
            saved `"hidden"`, and ONE Escape (both traps listen on `document`) unmounts them in the
            same commit and leaves the page unscrollable with no dialog open. Never stacking them
            is what makes that unreachable; a refcount is the upstream fix (#1058/#1059 batch).
            Pinned by group-consult-day-panel.spec.tsx's scroll-lock test.
          */
            onRegisterClick={(event) => {
              group.setSelectedGroupDayKey(null);
              registration.handleRegisterClick(event);
            }}
            onClose={() => group.setSelectedGroupDayKey(null)}
          />
        ) : null}

        {/*
        Webinar registration modal — confirms session time; name/email from link when provided.
        `key` remounts it per session so the modal's own field state starts empty for each one;
        it replaces the `key` that used to sit on the inner <form> (see RegisterWebinarModal).
      */}
        {registration.registeringWebinarId && (
          <RegisterWebinarModal
            key={registration.registeringWebinarId}
            webinarId={registration.registeringWebinarId}
            event={group.events.find((e) => e.id === registration.registeringWebinarId)}
            timeZone={group.displayTimeZone}
            memberDetails={memberDetails}
            phoneDisplay={registration.registerPhoneDisplay}
            onPhoneDisplayChange={registration.setRegisterPhoneDisplay}
            registerError={registration.registerError}
            registerLoading={registration.registerLoading}
            onSubmit={registration.handleRegisterFormSubmit}
            onCancel={registration.closeRegisterModal}
          />
        )}

        {registration.registrationConfirmation && (
          <RegistrationConfirmationDialog
            confirmation={registration.registrationConfirmation}
            onClose={registration.dismissRegistrationConfirmation}
          />
        )}
      </ConsultContentShell>
    </BookingReportContext.Provider>
  );
}
